import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';
import axios from 'axios';
import { getCachedSettings, cacheSettings, preloadImages } from '../utils/settingsCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_NAV_LINKS = [
  { path: '/', label_en: 'Home', label_hi: 'होम' },
  { path: '/services', label_en: 'Services', label_hi: 'सेवाएँ' },
  { path: '/doctors', label_en: 'Doctors', label_hi: 'डॉक्टर' },
  { path: '/diagnostic-centre', label_en: 'Diagnostic Centre', label_hi: 'निदान केंद्र' },
  { path: '/blood-bank', label_en: 'Blood Bank', label_hi: 'ब्लड बैंक' },
  { path: '/gallery', label_en: 'Gallery', label_hi: 'गैलरी' },
  { path: '/testimonials', label_en: 'Testimonials', label_hi: 'प्रशंसापत्र' },
  { path: '/jobs', label_en: 'Careers', label_hi: 'करियर' },
  { path: '/about', label_en: 'About Us', label_hi: 'हमारे बारे में' },
  { path: '/contact', label_en: 'Contact', label_hi: 'संपर्क' },
];

const HospitalName = ({ language, settings, isMobile }) => {
  const name = language === 'en' ? settings.hospital_name_en : settings.hospital_name_hi;
  const hospitalSuffix = language === 'en' ? '(Hospital)' : '(अस्पताल)';
  const allWords = (name || '').split(' ').filter(w => w);
  const hasHospitalSuffix = allWords.some(w => w.includes('('));
  const words = hasHospitalSuffix ? allWords : [...allWords, hospitalSuffix];
  const wordStyles = settings.hospital_name_word_styles || [];
  const baseFontSize = settings.header_font_size || 18;
  // Scale down on mobile so it never breaks the layout (still readable)
  const fontSize = isMobile ? Math.min(baseFontSize, 14) : baseFontSize;

  if (words.length === 0) {
    return <span className="font-heading font-light uppercase tracking-ultra-wide" style={{ fontSize: `${fontSize}px` }}>Hospital</span>;
  }
  return words.map((word, index) => {
    const isParenthetical = word.includes('(') || word.includes(')');
    const style = wordStyles.find(s => s.word === word && s.index === index) ||
                  wordStyles.find(s => s.index === index) ||
                  { color: '#000000', font: 'Playfair Display, serif', fontWeight: '400' };
    const wordFontSize = isParenthetical ? fontSize - 2 : fontSize;
    return (
      <span key={`${word}-${index}`} style={{
        color: style.color || '#000000',
        fontFamily: style.font || 'Playfair Display, serif',
        fontWeight: style.fontWeight || '400',
        fontSize: `${wordFontSize}px`,
        letterSpacing: '0.1em',
      }}>
        {word}
      </span>
    );
  });
};

const Navbar = () => {
  const { language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(getCachedSettings);
  const [navLinks, setNavLinks] = useState(DEFAULT_NAV_LINKS);
  const [scrolled, setScrolled] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => () => { isMounted.current = false; }, []);

  const fetchData = useCallback(async () => {
    try {
      const [settingsRes, navRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/nav-menu`),
      ]);
      if (!isMounted.current) return;
      const newSettings = { ...getCachedSettings(), ...settingsRes.data };
      setSettings(newSettings);
      cacheSettings(newSettings);
      preloadImages([newSettings.logo_url, newSettings.hero_image_url].filter(Boolean));
      if (navRes.data && navRes.data.length > 0) {
        setNavLinks(navRes.data.map(item => ({
          path: item.path, label_en: item.label_en, label_hi: item.label_hi,
        })));
      }
    } catch (error) {
      console.error('Failed to fetch navbar data:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fetchData]);

  // Close mobile menu on route change
  useEffect(() => { setIsOpen(false); }, [location.pathname]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white'
      }`}
      data-testid="main-navbar"
    >
      {/* Tier 1: logo + actions */}
      <div className="border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 flex items-center justify-between lg:justify-center gap-2 lg:relative">
          <Link
            to="/"
            className="flex items-center gap-2 sm:gap-3 lg:flex-col lg:gap-2 min-w-0"
            data-testid="navbar-logo-link"
          >
            {settings.logo_url && (
              <img
                src={settings.logo_url}
                alt="Hospital Logo"
                width={56}
                height={56}
                className="h-9 w-9 sm:h-11 sm:w-11 lg:h-14 lg:w-14 object-contain flex-shrink-0"
                data-testid="hospital-logo"
              />
            )}
            <span
              className="leading-tight flex flex-wrap gap-x-1 sm:gap-x-1.5 items-baseline justify-start lg:justify-center text-left lg:text-center min-w-0 lg:max-w-none max-w-[230px] sm:max-w-[360px]"
              data-testid="hospital-name"
            >
              <HospitalName language={language} settings={settings} isMobile={true} />
            </span>
          </Link>

          {/* Action cluster: lang toggle + mobile menu trigger */}
          <div className="flex items-center gap-1.5 sm:gap-2 lg:absolute lg:right-4 lg:top-1/2 lg:-translate-y-1/2 flex-shrink-0">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 border border-neutral-200 text-neutral-700 hover:bg-neutral-50 transition-colors text-[10px] sm:text-xs uppercase tracking-ultra-wide font-body"
              data-testid="language-toggle-btn"
              aria-label="Toggle language"
            >
              <Globe className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              <span>{language === 'en' ? 'EN' : 'हिं'}</span>
            </button>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 hover:bg-neutral-50 transition-colors -mr-1"
              data-testid="mobile-menu-toggle"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
            >
              {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Tier 2: Desktop horizontal nav */}
      <div className="hidden lg:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-8 py-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-xs uppercase tracking-ultra-wide font-body font-light transition-colors duration-300 pb-0.5 ${
                  location.pathname === link.path
                    ? 'text-brand-teal border-b border-brand-teal'
                    : 'text-neutral-500 hover:text-brand-navy'
                }`}
                data-testid={`nav-link-${link.path.substring(1) || 'home'}`}
              >
                {language === 'en' ? link.label_en : link.label_hi}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile drop-down menu */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t border-neutral-100 max-h-[calc(100vh-72px)] overflow-y-auto" data-testid="mobile-nav-menu">
          <div className="px-5 py-4 space-y-0">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block py-3 text-xs uppercase tracking-ultra-wide font-body font-light transition-colors border-b border-neutral-100 last:border-b-0 ${
                  location.pathname === link.path
                    ? 'text-brand-teal font-normal'
                    : 'text-neutral-600 hover:text-brand-navy'
                }`}
                data-testid={`mobile-nav-link-${link.path.substring(1) || 'home'}`}
              >
                {language === 'en' ? link.label_en : link.label_hi}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
